<template>
  <main class="login-help">
    <header class="help-header">
      <b-link
        class="brand"
        :to="{ name: 'auth-login-local' }"
      >
        <vuexy-logo />
        <span>IpsosCX</span>
      </b-link>

      <div class="header-actions">
        <div
          class="language-switch"
          role="group"
          :aria-label="copy.language"
        >
          <button
            v-for="option in languages"
            :key="option.code"
            type="button"
            :class="{ active: locale === option.code }"
            @click="setLocale(option.code)"
          >
            {{ option.label }}
          </button>
        </div>
        <b-button
          variant="outline-primary"
          :to="{ name: 'auth-login-local' }"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            class="mr-50"
          />
          {{ copy.backToLogin }}
        </b-button>
      </div>
    </header>

    <section class="help-intro">
      <div>
        <span class="eyebrow">CXPLUS</span>
        <h1>{{ copy.title }}</h1>
        <p>{{ copy.intro }}</p>
      </div>
      <aside class="browser-note">
        <img
          :src="chromeIcon"
          alt="Google Chrome"
        >
        <div>
          <strong>{{ copy.browserTitle }}</strong>
          <p>{{ copy.browserText }}</p>
        </div>
      </aside>
    </section>

    <section
      class="steps"
      :aria-label="copy.title"
    >
      <article class="step-section">
        <div class="step-copy">
          <span class="step-number">01</span>
          <h2>{{ copy.loginTitle }}</h2>
          <ul>
            <li>{{ copy.loginStepOne }}</li>
            <li>{{ copy.loginStepTwo }}</li>
          </ul>
        </div>

        <div class="screen-preview login-preview">
          <div class="preview-brand">
            <vuexy-logo />
            <span>IpsosCX</span>
          </div>
          <div class="preview-layout">
            <div class="mock-form">
              <h3>{{ copy.welcome }}</h3>
              <p>{{ copy.newPasswordPrompt }}</p>
              <label>{{ copy.email }}</label>
              <div class="mock-input">
                name@example.com
              </div>
              <div class="mock-label-row">
                <label>{{ copy.password }}</label>
                <span>{{ copy.forgotPassword }}</span>
              </div>
              <div class="mock-input">
                ••••••••••
              </div>
              <div class="mock-button">
                {{ copy.signIn }}
              </div>
            </div>
          </div>
        </div>
      </article>

      <article class="step-section reverse">
        <div class="step-copy">
          <span class="step-number">02</span>
          <h2>{{ copy.passwordTitle }}</h2>
          <ul>
            <li>{{ copy.passwordStepOne }}</li>
            <li>{{ copy.passwordStepTwo }}</li>
            <li>{{ copy.passwordStepThree }}</li>
          </ul>
        </div>

        <div class="password-flow">
          <div class="screen-preview reset-preview">
            <img
              :src="forgotImage"
              :alt="copy.resetImageAlt"
            >
            <div class="mock-form">
              <h3>{{ copy.forgotPassword }}</h3>
              <p>{{ copy.resetIntro }}</p>
              <label>{{ copy.email }}</label>
              <div class="mock-input">
                name@example.com
              </div>
              <div class="mock-button">
                {{ copy.sendLink }}
              </div>
            </div>
          </div>
          <div class="mail-preview">
            <feather-icon
              icon="MailIcon"
              size="28"
            />
            <div>
              <span>{{ copy.mailFrom }}</span>
              <strong>{{ copy.mailSubject }}</strong>
              <small>{{ copy.mailBody }}</small>
            </div>
          </div>
        </div>
      </article>
    </section>

    <footer class="help-footer">
      <div>
        <strong>{{ copy.stillNeedHelp }}</strong>
        <span>{{ copy.contactAdmin }}</span>
      </div>
      <b-button
        variant="primary"
        :to="{ name: 'auth-login-local' }"
      >
        {{ copy.goToLogin }}
        <feather-icon
          icon="ArrowRightIcon"
          class="ml-50"
        />
      </b-button>
    </footer>
  </main>
</template>

<script>
import VuexyLogo from '@core/layouts/components/Logo.vue'
import { BButton, BLink } from 'bootstrap-vue'
import chromeIcon from '@/assets/images/icons/google-chrome.png'
import forgotImage from '@/assets/images/pages/forgot-password.png'

export default {
  components: {
    VuexyLogo,
    BButton,
    BLink,
  },
  data() {
    return {
      locale: localStorage.getItem('loginHelpLocale') || localStorage.getItem('locale') || 'tr',
      languages: [
        { code: 'tr', label: 'TR' },
        { code: 'en', label: 'EN' },
      ],
      chromeIcon,
      forgotImage,
      content: {
        tr: {
          language: 'Dil seçimi',
          backToLogin: 'Girişe dön',
          title: 'Giriş ve şifre yardımı',
          intro: 'CxPlus hesabınıza giriş yapmak, ilk şifrenizi oluşturmak veya şifrenizi yenilemek için aşağıdaki adımları izleyin.',
          browserTitle: 'Tarayıcı önerisi',
          browserText: 'Bağlantıları açarken Google Chrome kullanmanızı öneririz.',
          loginTitle: 'Sisteme giriş',
          loginStepOne: 'E-posta adresinizi ve şifrenizi ilgili alanlara yazın.',
          loginStepTwo: 'Giriş düğmesine basarak CxPlus hesabınıza erişin.',
          welcome: "CxPlus'a Hoşgeldiniz!",
          newPasswordPrompt: 'Daha önce şifre oluşturmadıysanız bağlantıya tıklayın.',
          email: 'E-posta',
          password: 'Şifre',
          forgotPassword: 'Şifremi Unuttum?',
          signIn: 'Giriş',
          loginImageAlt: 'CxPlus giriş ekranı örneği',
          passwordTitle: 'İlk şifreyi oluşturma veya yenileme',
          passwordStepOne: 'İlk kez giriş yapıyorsanız “buraya tıklayın” bağlantısını kullanın. Mevcut şifrenizi unuttuysanız “Şifremi Unuttum?” bağlantısını seçin.',
          passwordStepTwo: 'E-posta adresinizi yazıp sıfırlama bağlantısını gönderin.',
          passwordStepThree: 'E-postanıza gelen bağlantıyı Chrome ile açın. Yeni şifreniz en az 8 karakter, bir büyük harf, bir küçük harf ve bir özel karakter içermelidir.',
          resetImageAlt: 'Şifre sıfırlama ekranı örneği',
          resetIntro: 'Şifre sıfırlama bağlantısı için e-posta adresinizi girin.',
          sendLink: 'Sıfırlama linki gönder',
          mailFrom: 'Cx Plus',
          mailSubject: 'Password Reset / Şifre Yenileme',
          mailBody: 'Yeni şifre oluşturmak için e-postadaki bağlantıya tıklayın.',
          stillNeedHelp: 'Hâlâ yardıma mı ihtiyacınız var?',
          contactAdmin: 'Hesabınız veya e-posta adresinizle ilgili sorunlarda sistem yöneticinizle iletişime geçin.',
          goToLogin: 'Giriş ekranına git',
        },
        en: {
          language: 'Language selection',
          backToLogin: 'Back to login',
          title: 'Login and password help',
          intro: 'Follow these steps to sign in to CxPlus, create your first password, or reset your current password.',
          browserTitle: 'Browser recommendation',
          browserText: 'We recommend using Google Chrome when opening links.',
          loginTitle: 'Sign in to the system',
          loginStepOne: 'Enter your email address and password in the relevant fields.',
          loginStepTwo: 'Select Sign in to access your CxPlus account.',
          welcome: 'Welcome to CxPlus!',
          newPasswordPrompt: 'If you have not created a password before, select the link.',
          email: 'Email',
          password: 'Password',
          forgotPassword: 'Forgot Password?',
          signIn: 'Sign in',
          loginImageAlt: 'Example CxPlus login screen',
          passwordTitle: 'Create or reset your password',
          passwordStepOne: 'For your first login, use the “click here” link. If you forgot an existing password, select “Forgot Password?”.',
          passwordStepTwo: 'Enter your email address and send the password reset link.',
          passwordStepThree: 'Open the link sent to your email in Chrome. Your new password must contain at least 8 characters, one uppercase letter, one lowercase letter, and one special character.',
          resetImageAlt: 'Example password reset screen',
          resetIntro: 'Enter your email address to receive password reset instructions.',
          sendLink: 'Send reset link',
          mailFrom: 'Cx Plus',
          mailSubject: 'Password Reset / Şifre Yenileme',
          mailBody: 'Select the link in the email to create your new password.',
          stillNeedHelp: 'Still need help?',
          contactAdmin: 'Contact your system administrator for account or email address issues.',
          goToLogin: 'Go to login',
        },
      },
    }
  },
  computed: {
    copy() {
      return this.content[this.locale] || this.content.tr
    },
  },
  methods: {
    setLocale(locale) {
      this.locale = locale
      localStorage.setItem('loginHelpLocale', locale)
    },
  },
}
</script>

<style lang="scss" scoped>
.login-help {
  --ink: #15233a;
  --muted: #667085;
  --line: #d8dee8;
  --primary: #4059ad;
  --accent: #00a6a6;
  min-height: 100vh;
  padding: 28px clamp(20px, 5vw, 76px) 36px;
  color: var(--ink);
  background-color: #f7f9fc;
}

.help-header,
.help-intro,
.step-section,
.help-footer {
  width: min(1280px, 100%);
  margin-right: auto;
  margin-left: auto;
}

.help-header,
.header-actions,
.brand,
.help-intro,
.browser-note,
.mock-label-row,
.mail-preview,
.help-footer {
  display: flex;
  align-items: center;
}

.help-header {
  justify-content: space-between;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--line);
}

.brand {
  color: var(--primary);
  font-size: 20px;
  font-weight: 700;
  gap: 10px;
}

.brand svg,
.preview-brand svg {
  width: 28px;
  height: 28px;
}

.header-actions {
  gap: 14px;
}

.language-switch {
  display: inline-flex;
  padding: 3px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: #fff;
}

.language-switch button {
  min-width: 42px;
  padding: 7px 10px;
  border: 0;
  border-radius: 4px;
  color: var(--muted);
  background: transparent;
  font-weight: 600;
}

.language-switch button.active {
  color: #fff;
  background: var(--primary);
}

.help-intro {
  justify-content: space-between;
  gap: 48px;
  padding: 64px 0 48px;
}

.eyebrow {
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
}

h1 {
  margin: 8px 0 14px;
  font-size: clamp(34px, 5vw, 58px);
  line-height: 1.05;
  letter-spacing: 0;
}

.help-intro > div > p {
  max-width: 720px;
  margin: 0;
  color: var(--muted);
  font-size: 18px;
  line-height: 1.6;
}

.browser-note {
  flex: 0 0 310px;
  gap: 18px;
  padding: 18px 20px;
  border: 1px solid var(--accent);
  border-radius: 8px;
  background: #fff;
}

.browser-note img {
  width: 44px;
  height: 44px;
}

.browser-note p {
  margin: 4px 0 0;
  color: var(--muted);
}

.steps {
  border-top: 1px solid var(--line);
}

.step-section {
  display: grid;
  grid-template-columns: minmax(260px, .7fr) minmax(520px, 1.3fr);
  gap: clamp(36px, 6vw, 88px);
  align-items: center;
  padding: 70px 0;
  border-bottom: 1px solid var(--line);
}

.step-section.reverse {
  grid-template-columns: minmax(520px, 1.3fr) minmax(260px, .7fr);
}

.step-section.reverse .step-copy {
  grid-column: 2;
  grid-row: 1;
}

.step-section.reverse .password-flow {
  grid-column: 1;
  grid-row: 1;
}

.step-number {
  display: block;
  color: var(--accent);
  font-size: 14px;
  font-weight: 700;
}

h2 {
  margin: 9px 0 22px;
  font-size: 30px;
  letter-spacing: 0;
}

.step-copy ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.step-copy li {
  position: relative;
  margin-bottom: 16px;
  padding-left: 25px;
  color: #344054;
  font-size: 16px;
  line-height: 1.6;
}

.step-copy li::before {
  position: absolute;
  top: 8px;
  left: 0;
  width: 9px;
  height: 9px;
  border: 2px solid var(--primary);
  content: '';
}

.screen-preview {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 48px rgba(21, 35, 58, .1);
}

.preview-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 18px;
  border-bottom: 1px solid #eef1f5;
  color: var(--primary);
  font-weight: 700;
}

.preview-layout {
  max-width: 460px;
  min-height: 350px;
  margin: 0 auto;
}

.mock-form {
  align-self: center;
  padding: 28px;
}

.mock-form h3 {
  margin: 0 0 6px;
  font-size: 18px;
}

.mock-form p {
  margin-bottom: 20px;
  color: var(--muted);
  font-size: 11px;
}

.mock-form label,
.mock-label-row span {
  margin-bottom: 5px;
  color: #475467;
  font-size: 10px;
}

.mock-label-row {
  justify-content: space-between;
}

.mock-label-row span {
  color: var(--primary);
}

.mock-input {
  height: 34px;
  margin-bottom: 14px;
  padding: 9px 11px;
  border: 1px solid var(--line);
  border-radius: 4px;
  color: #98a2b3;
  font-size: 10px;
}

.mock-button {
  padding: 9px;
  border-radius: 4px;
  color: #fff;
  background: var(--primary);
  text-align: center;
  font-size: 11px;
  font-weight: 600;
}

.password-flow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 18px;
  align-items: end;
}

.reset-preview {
  display: grid;
  grid-template-columns: 180px minmax(220px, 1fr);
  align-items: center;
  min-height: 300px;
  padding: 28px;
}

.reset-preview > img {
  width: 100%;
}

.mail-preview {
  gap: 14px;
  padding: 20px;
  border-left: 4px solid var(--accent);
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(21, 35, 58, .09);
}

.mail-preview svg {
  flex: 0 0 auto;
  color: var(--accent);
}

.mail-preview div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.mail-preview span,
.mail-preview small {
  color: var(--muted);
}

.mail-preview strong {
  margin: 3px 0;
  font-size: 12px;
}

.mail-preview small {
  font-size: 10px;
}

.help-footer {
  justify-content: space-between;
  gap: 28px;
  padding-top: 36px;
}

.help-footer div {
  display: flex;
  flex-direction: column;
}

.help-footer span {
  margin-top: 4px;
  color: var(--muted);
}

@media (max-width: 991px) {
  .help-intro,
  .help-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .browser-note {
    width: 100%;
    flex-basis: auto;
  }

  .step-section,
  .step-section.reverse {
    grid-template-columns: 1fr;
  }

  .step-section.reverse .step-copy,
  .step-section.reverse .password-flow {
    grid-column: 1;
    grid-row: auto;
  }

  .step-section.reverse .step-copy {
    grid-row: 1;
  }
}

@media (max-width: 650px) {
  .login-help {
    padding: 20px 16px 28px;
  }

  .help-header {
    align-items: flex-start;
    gap: 18px;
  }

  .header-actions {
    align-items: flex-end;
    flex-direction: column;
  }

  .help-intro {
    padding: 42px 0 34px;
  }

  .step-section {
    gap: 26px;
    padding: 44px 0;
  }

  .reset-preview,
  .password-flow {
    grid-template-columns: 1fr;
  }

  .reset-preview {
    padding: 22px;
  }

  .reset-preview > img {
    width: 150px;
    margin: 0 auto;
  }
}
</style>
